<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { defineAsyncComponent, inject, nextTick, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ChevronRight, Flame, RefreshCcw, Shuffle } from 'lucide-vue-next'
import ProgressSpinner from 'primevue/progressspinner'
import RainbowButtonBig from './utils/RainbowButtonBig.vue'
import { useScrollDirectionEvent } from '@/composables/useScrollDirectionEvent'
import { useResizeListener } from '@/composables/useResizeListener'
import { useAutoScroll } from '@/composables/useAutoScroll'
import { useSearchStore } from '@/stores/searchStoreV2'
import { useCompatibilityStore } from '@/stores/compatibilityStore'
import { useImageStore } from '@/stores/imageStore'
import { useFullscreenStore } from '@/stores/fullscreenStore'
import type { IImage } from '@/types'
import { DynamicModalType, Target } from '@/types'
import type { RequestRouter } from '@/request/requestRouter'
import { RequestAction } from '@/request/requestRouter'
import { useSettingStore } from '@/stores/settingStore'
import Footer from '@/components/Footer.vue'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { useAuthStore } from '@/stores/authStore'

const Image = defineAsyncComponent(() => import('@/components/image/gallery/Image.vue'))
const SearchPanel = defineAsyncComponent(() => import('@/components/search/SearchPanel.vue'))

const router = useRouter()
const request = inject<RequestRouter>('request')
const { t } = useI18n()
const route = useRoute()
const { stop: stopAutoScroll } = useAutoScroll()
const fullscreenStore = useFullscreenStore()
const compat = useCompatibilityStore()
const imageStore = useImageStore()
const { disable } = useScrollDirectionEvent()
const searchStore = useSearchStore()
const { openPanel, imagesFound, mode, searching, shuffle, prompts } = storeToRefs(searchStore)
const { mobileDevice } = storeToRefs(compat)
const { computedScreenWidth } = useResizeListener()

const { isFullscreen } = storeToRefs(fullscreenStore)
const { images, userGalleryProfile, refreshRequired } = storeToRefs(imageStore)

const landingImages = ref<string[]>([
	'01752efa-25d0-11f0-a7d8-3efd045898fa',
	'59b71b4e-2677-11f0-a7d8-3efd045898fa',
	'9008e9c2-25ad-11f0-a7d8-3efd045898fa',
	'c905d1d0-25c7-11f0-aca8-3efd045898fa',
	'14c165f2-25c9-11f0-aca8-3efd045898fa',
	'437fd2d4-25c4-11f0-aca8-3efd045898fa',
	'8301d5b8-25a8-11f0-9287-5220dc440b66',
	'd3e398cc-25a8-11f0-bb20-5220dc440b66',
	'7a81b6dc-267b-11f0-aca8-3efd045898fa',
	'7eb827a4-25c2-11f0-a7d8-3efd045898fa',
	'549b66b8-25a7-11f0-9287-5220dc440b66',
	'6828a8ce-2662-11f0-a7d8-3efd045898fa',
	'8fe33248-2663-11f0-a7d8-3efd045898fa',
	'6d31f518-25aa-11f0-a7d8-3efd045898fa',
	'f0f477b0-267a-11f0-aca8-3efd045898fa',
	'7238fd34-25c0-11f0-aca8-3efd045898fa',
	'24825b12-25cf-11f0-aca8-3efd045898fa',
	'b25238b2-267a-11f0-aca8-3efd045898fa',
	'c0c90e36-2678-11f0-a7d8-3efd045898fa',
	'7d0c9f8e-25a9-11f0-bb20-5220dc440b66',
	'88d73370-25d0-11f0-aca8-3efd045898fa',
	'30317e9c-25c6-11f0-aca8-3efd045898fa',
	'8c2a4160-25d1-11f0-a7d8-3efd045898fa',
	'c103033e-25bf-11f0-a7d8-3efd045898fa',
])

watch(() => openPanel.value, async () => {
	if (computedScreenWidth.value >= 1024 || mobileDevice.value) {
		disable.value = true
		await nextTick()
		setTimeout(() => {
			disable.value = false
		}, 200)
	}
})

onBeforeRouteLeave(async () => {
	if (!isFullscreen.value) {
		stopAutoScroll()
	}
})

function refresh() {
	refreshRequired.value = false
	useSettingStore().refreshEsQueryMeta('creations')
	imageStore.reset()
	searchStore.search()
}

watch(() => route.path, async () => {
	if (route.name === Target.Gallery || route.name === Target.Collection || route.name === Target.Creations || route.name === Target.UserGallery) {
		if (route.params.id) {
			userGalleryProfile.value.id = Number(route.params.id)
			const res = await request?.exec(RequestAction.GetUserProfile, { routeParams: { userId: userGalleryProfile.value.id } })
			if (res) {
				userGalleryProfile.value = res
				if (route.params.username !== userGalleryProfile.value.username) {
					router.replace({
						name: 'UserGallery',
						params: {
							id: userGalleryProfile.value.id,
							username: userGalleryProfile.value.username,
						},
					})
				}
			}
		}
		else {
			userGalleryProfile.value.id = 0
		}
		mode.value = route.name
		await nextTick()
		if (!images.value.length) {
			await imageStore.loadImages(userGalleryProfile.value.id)
		}
	}
}, { immediate: true })
</script>

<template>
	<div class="flex flex-col flex-nowrap menu:mt-[70px] mt-[120px] relative px-4">
		<div class="flex flex-row flex-nowrap">
			<div
				class="flex flex-col justify-between min-h-[calc(100dvh-50px)]"
				:class="openPanel && !mobileDevice ? '3xl:!w-[80%] xl:!w-9/12 !w-8/12' : 'w-full'"
			>
				<div
					class="w-full flex flex-wrap justify-center relative gap-1 pt-2"
				>
					<template v-if="!useAuthStore().isAuth() && mode === Target.Gallery">
						<Image
							v-for="(uuid, index) in landingImages"
							:key="index"
							:data="{ uuid } as IImage"
							:landing="true"
						/>
						<div class="w-full flex-center mt-4">
							<Button
								:label="t('common.signup_more')"
								icon="pi pi-chevron-right"
								icon-pos="right"
								class="button-orange-shade !gap-0 !text-lg"
								@click="router.push('/account/signup')"
							/>
						</div>
					</template>
					<template v-else-if="useAuthStore().isAuth()">
						<div class="flex-center w-full my-2">
							<Button
								class="button-slate !rounded-full tracking-tight !rounded-r-none"
								:class="{
									'!bg-slate-700 !text-slate-400': shuffle.current,
								}"
								@click="shuffle.current = false"
							>
								<template #default>
									<Flame class="lucide-button-icon" />
									{{ t('common.newest') }}
								</template>
							</Button>
							<Button
								class="button-slate !rounded-full !rounded-l-none !border-l-0"
								:class="{
									'!bg-slate-700 !text-slate-400': !shuffle.current,
								}"
								:disabled="!!prompts.opt.length"
								@click="shuffle.current = true"
							>
								<template #default>
									<Shuffle class="lucide-button-icon" />
									{{ t('common.shuffle') }}
								</template>
							</Button>
						</div>
						<div
							v-if="refreshRequired && route.name === Target.Creations"
							class="flex-center my-2 w-full"
						>
							<Button
								class="button-orange-shade !rounded-full"
								@click="refresh"
							>
								<RefreshCcw class="lucide-button-icon" />
								{{ t('common.refresh') }}
							</Button>
						</div>
						<Image
							v-for="(image, index) in images"
							:key="image.key"
							:data="image"
							@on-image-zoom="fullscreenStore.routeTo(image, index)"
						/>
					</template>

					<div
						v-if="searching"
						class="w-screen h-full flex-center absolute top-[200px] flex flex-col gap-2 text-center"
					>
						<ProgressSpinner
							style="width: 80px; height: 80px"
							stroke-width="3"
							animation-duration=".6s"
							aria-label="Searching in progress"
						/>
					</div>
					<div
						v-else-if="!searching && images && !images.length && !imagesFound && useAuthStore().isAuth()"
						class="w-screen h-full flex-center absolute top-[200px] flex flex-col gap-2 text-center"
					>
						<span class="text-2xl font-semibold">{{ t('common.no_results') }}</span>
						<i18n-t
							scope="global"
							keypath="broaden_search.message"
							tag="span"
							class="leading-relaxed"
						>
							<template #search>
								<span
									class="hyperlink-purple"
									@click="openPanel = !openPanel"
								>{{ t('broaden_search.search') }}</span>
							</template>
							<template #selection_of_styles>
								<span
									class="hyperlink-purple"
									@click="useDynamicModalStore().showModal(DynamicModalType.SelectFilterStyles, {
										props: {
											mode: 'filter',
										},
									})"
								>{{ t('broaden_search.selection_of_styles') }}</span>
							</template>
						</i18n-t>
					</div>
					<div
						v-else-if="mode === Target.Collection && !useAuthStore().isAuth()"
						class="text-slate-300 flex-col w-[540px] mb-20 h-screen flex items-center gap-10 mt-12"
					>
						<h1 class="font-extrabold text-3xl md:text-6xl text-center w-fit h-fit p-0 m-0">
							{{ t('menu.collection') }}
						</h1>

						<h3 class="m-0 p-0 text-base font-normal tracking-normal text-slate-300 text-center leading-relaxed">
							{{ t('collection.logout_msg') }}
						</h3>
						<RainbowButtonBig
							:label="t('common.signup_more')"
							:icon="ChevronRight"
							class="mt-10"
							@click="router.push('/account/signup')"
						/>
					</div>
					<div
						v-else-if="mode === Target.Creations && !useAuthStore().isAuth()"
						class="text-slate-300 flex-col w-[540px] mb-20 h-screen flex items-center gap-10 mt-12"
					>
						<h1 class="font-extrabold text-3xl md:text-6xl text-center w-fit h-fit p-0 m-0">
							{{ t('menu.creations') }}
						</h1>
						<h3 class="m-0 p-0 text-base font-normal tracking-normal text-slate-300 text-center leading-relaxed">
							{{ t('creations.logout_msg') }}
						</h3>
						<RainbowButtonBig
							:label="t('common.signup_more')"
							:icon="ChevronRight"
							class="mt-10"
							@click="router.push('/account/signup')"
						/>
					</div>

					<div
						v-if="useAuthStore().isAuth()"
						v-intersect="() => imageStore.loadImages(userGalleryProfile.id)"
						class="w-full h-[10px] mt-[-250px] -z-[1]"
					/>
				</div>
				<Footer />
			</div>

			<SearchPanel />
		</div>
	</div>
</template>
